import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { selectFromStorage } from '@adex/store/selectors/storage.selectors'
import { distinctUntilChanged, filter, map, mapTo, switchMap, take, tap } from 'rxjs/operators'
import { appRoutes, pathUrl } from '@adex/shared/routing/routes'
import { isEmpty, isNull } from '@adex/shared/utils/data'
import { selectUrl } from '@adex/store/selectors/router.selectors'
import { IUser } from '@adex/shared/models/user'
import { navigate } from '@adex/store/actions'
import { Intercom } from 'ng-intercom'
import { environment } from '../environments'
import { v4 as uuidv4 } from 'uuid'
import { HttpClient } from '@angular/common/http'
import { NzNotificationService } from 'ng-zorro-antd/notification'


@Component({
  selector: 'adex-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {

  constructor(private readonly store: Store,
              private readonly intercom: Intercom,
              private readonly http: HttpClient,
              private readonly notificationService: NzNotificationService) {
    this.redirect$.subscribe()
  }

  private lastUserId = -1
  private readonly currentUrl$ = this.store.select(selectUrl)
  private readonly user$ = this.store.select(selectFromStorage('user'))

  private readonly redirect$ = this.user$.pipe(
    tap(user => this.bootIntercom(user)),
    map(user => this.checkIsValidUser(user)),
    filter((isUser: boolean | null) => !isNull(isUser)),
    distinctUntilChanged(),
    map(isUser => !isUser ? appRoutes.app.integration.main : appRoutes.auth.login),
    switchMap(url => this.currentUrl$.pipe(
      filter(Boolean),
      take(1),
      filter((currentUrl: string) => pathUrl(currentUrl, 1) !== pathUrl(url, 1) || AppComponent.isNotFullRoure(currentUrl)),
      mapTo(url)
    )),
    distinctUntilChanged(),
    tap(url => this.store.dispatch(navigate({ commands: url })))
  )

  @ViewChild('tplContent') private tplContent

  private checkIsValidUser(user: IUser): boolean | null {
    const isUser = isEmpty(user)
    if (!isUser && !user.passwordSet) {
      this.store.dispatch(navigate({ commands: appRoutes.auth.setPassword }))
      return null
    }
    return isUser
  }

  private checkNewAppVersion() {
    this.http.get(`/assets/app-version.txt?t=${ Date.now() }`, { responseType: 'text' }).pipe(
      tap((version) => {
        if (version !== environment.version) {
          this.notificationService.template(this.tplContent, {
            nzDuration: 0
          })
        }
      })
    ).subscribe()
  }

  private bootIntercom(user?: IUser) {
    let userId = window.localStorage.getItem('intercomTemporaryUserId')

    if (!userId && !user?.id) {
      userId = uuidv4()
      window.localStorage.setItem('intercomTemporaryUserId', userId)
    } else if (user?.id) {
      userId = user?.id.toString()
    }

    if (this.lastUserId === user?.id) return
    this.lastUserId = user?.id
    this.intercom.shutdown()
    this.intercom.boot({
      app_id: environment.intercomAppId,
      email: user?.email,
      user_id: userId,
      created_at: user?.createdAt,
      adp_advertiser_id: user?.adpAdvertiserId,
      widget: {
        activator: '#intercom'
      }
    })
  }

  public ngOnInit(): void {
    setInterval(() => {
      this.checkNewAppVersion()
    }, 1800000)
  }

  public reload() {
    window.location.reload()
  }

  private static isNotFullRoure(url) {
    const notFull = [ appRoutes.auth.main, appRoutes.app.main ]
    return notFull.includes(url)
  }
}
